import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

/**
 * Need to duplicate this function here because there is a circular dependency between utils and constants in test environment.
 *
 * @return {string}
 */
const slugifyString = (value, { substitutions } = {}) => {
	let slug = value;

	if (isNonEmptyString(value)) {
		slug = value.toLowerCase();

		if (isNonEmptyArray(substitutions)) {
			substitutions
				.filter(substitution => {
					return isNonEmptyArray(substitution) && substitution.length === 2;
				})
				.forEach(([ search, replace ]) => {
					slug = slug.replace(search, replace);
				});
		}

		slug = slug.replace(/[^a-z0-9\s-]/g, "")
			.replace(/[\s]+/g, "-");
	}

	return slug;
};


/* Env Variables */
export const BUILD_TIME = process.env.REACT_APP_BUILD_TIME;
export const IS_DEV = process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test" || process.env.REACT_APP_ENVIRONMENT === "develop";
export const IS_DEMO = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "demo";
export const IS_FT = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "ft";
export const IS_ENG = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "eng";
export const IS_LIVE = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "live";
export const IS_PROD_SG = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "prod-sg";
export const IS_QA = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "qa";
export const IS_RGRS = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "rgrs";
export const IS_TRAIN_SG = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "train-sg";
export const IS_UAT = !IS_DEV && process.env.REACT_APP_ENVIRONMENT === "uat";

export const IS_PROD = !IS_DEV && !IS_QA;

/* Feature Toggles */
export const HIDE_FROM_RELEASE = IS_DEV;
export const PROD_ENV = IS_PROD_SG || IS_LIVE;

/* Paging */
export const DEFAULT_PAGE_LIMIT = 10;
export const DEFAULT_SEARCH_LIMIT = 20;
export const MAX_PAGE_LIMIT = 999;

/* Routes */
export const ADMINISTRATION_API_ROUTE = `${process.env.REACT_APP_ADMIN_URL}/api/v2`;
export const AWARDS_BASE_ROUTE = "/awards";
export const SUBAWARDS_BASE_ROUTE = "/subawards";
export const FORMS_BASE_ROUTE = "/forms";
export const PROJECTS_BASE_ROUTE = "/projects";
export const PROPOSALS_BASE_ROUTE = "/proposals";
export const RESOLUTIONS_BASE_ROUTE = "/resolutions";
export const MODIFICATIONS_BASE_ROUTE = "/modifications";
export const ATTACHMENT_TYPES_BASE_ROUTE = "/attachment-types";
export const BUDGET_SETTINGS_BASE_ROUTE = "/budget-settings";
export const CERTIFICATION_TEXT_BASE_ROUTE = "/certification-text";
export const MANAGE_RT_ROLES_ROUTE = "/research-team-roles";
export const LOGOUT_ROUTE = "/logout";
export const LOGOUT_REDIRECT_ROUTE = `https://${process.env.REACT_APP_AUTH_DOMAIN}/logout`;
export const REPORTING_PROPOSALS_ROUTE = "/reporting/proposals";
export const REPORTING_AWARDS_ROUTE = "/reporting/awards";
export const REPORTING_SUBAWARDS_ROUTE = "/reporting/subawards";
export const TENANT_URL = window.location.href.split("/sp").shift();
export const BUDGET_BUILDER_ROUTE = `${TENANT_URL}/sp/bb`;
export const TENANT_BASE_ROUTE = `${TENANT_URL}/sp`;

/* Page Titles */
export const PAGE_TITLES = {
	PROPOSAL_DASHBOARD: "Proposal Dashboard | Cayuse Sponsored Projects",
	PROJECT_DASHBOARD: "Project Dashboard | Cayuse Sponsored Projects",
	AWARDS_DASHBOARD: "Awards Dashboard | Cayuse Sponsored Projects",
	SUBAWARDS_DASHBOARD: "Subawards Dashboard | Cayuse Sponsored Projects",
	REPORTING_AWARDS_DASHBOARD: "Award Reporting Dashboard | Cayuse Sponsored Projects",
	REPORTING_SUBAWARDS_DASHBOARD: "Subaward Reporting Dashboard | Cayuse Sponsored Projects",
	REPORTING_PROPOSALS_DASHBOARD: "Proposal Reporting Dashboard | Cayuse Sponsored Projects",
	AWARD_DETAIL: "Award Detail | Cayuse Sponsored Projects",
	SUBAWARDS_DETAIL: "Subaward Detail | Cayuse Sponsored Projects",
	FORMS: "Form Manager | Cayuse Sponsored Projects",
	PROPOSAL_DETAIL: "Proposal Detail | Cayuse Sponsored Projects",
	PROJECT_DETAIL: "Project Detail | Cayuse Sponsored Projects",
	RESOLUTIONS: "Manage Resolutions | Cayuse Sponsored Projects",
	MODIFICATIONS: "Manage Modifications | Cayuse Sponsored Projects",
	ATTACHMENT_TYPES: "Manage Attachment Types | Cayuse Sponsored Projects",
	BUDGET_SETTINGS: "Budget Settings | Cayuse Sponsored Projects",
	CERTIFICATION_TEXT: "Manage Certification Text | Cayuse Sponsored Projects",
	MANAGE_RT_ROLES: "Manage Research Team Roles | Cayuse Sponsored Projects",
	PROPOSAL_TASK: "SP Proposal Task | Cayuse Sponsored Projects",
	PROPOSAL_MILESTONE: "SP Proposal Milestone | Cayuse Sponsored Projects",
	AWARD_TASK: "SP Award Task | Cayuse Sponsored Projects",
	AWARD_MILESTONE: "SP Award Milestone | Cayuse Sponsored Projects",
	SUBAWARD_TASK: "SP Subaward Task | Cayuse Sponsored Projects",
	SUBAWARD_MILESTONE: "SP Subaward Milestone | Cayuse Sponsored Projects",
	PROJECT_TASK: "SP Project Task | Cayuse Sponsored Projects",
	PROJECT_MILESTONE: "SP Project Milestone | Cayuse Sponsored Projects",
};

/* Form types */
export const COLUMN_HEADER_KEYS = {
	PROPOSAL: "PROPOSAL",
	AWARD: "AWARD",
	SUBAWARD: "SUBAWARD",
	PROJECT: "PROJECT",
	BUDGET: "BUDGET",
	AGREEMENT: "AGREEMENT",
	MODIFICATIONS: "MODIFICATIONS",
	SUBAWARD_MODIFICATIONS: "SUBAWARD_MODIFICATIONS",
};

/* System generated dates keys */
export const SYS_DATES_KEYS = [ "createDate", "updateDate", "createdOn", "uploadedOn", "modificationDate" ];

/* API Endpoints */
export const API_S = "/api/s";
export const API_UNIT = "/api/unit";
export const API_ORGANIZATION = "/api/organization";
export const API_COLUMN_PREFERENCES = "/api/column-preferences";
export const API_FORMS = "/api/forms";
export const API_GROUP_ROLES = "/api/grouproles";
export const API_PROJECT = "/api/projects";
export const API_AGREEMENTS = "/api/agreements";
export const API_PROJECTS_SUMMARY = "/api/projects/summary";
export const API_NOTES = "/api/notes";
export const API_AWARD = "/api/awards";
export const API_SUBAWARD = "/api/v2/sub-awards";
export const API_AWARD_SUMMARY = "/api/awards/summary";
export const API_AWARD_FEED = "/api/award-feed";
export const API_SUBAWARD_FEED = "/api/v2/sub-award-feed";
export const API_SUBAWARD_SUMMARY = "/api/v2/sub-awards/summary";
export const API_BUDGET = "/api/budgetbuilder";
export const API_BUDGET_FEED = "/api/budgetbuilder-feed";
export const API_PROPOSAL = "/api/proposals";
export const API_PROPOSALS_SUMMARY = "/api/proposals/summary";
export const API_PROPOSAL_FEED = "/api/proposal-feed";
export const API_PROPOSAL_FEED_EXPORT = "/api/proposal-feed/export";
export const API_PROPOSAL_FEED_REPORT = "/api/proposal-feed/report";
export const API_RESEARCH_ROLES__DEPRECATED = "/api/proposals/research-team-roles";
export const API_REPORTING = `https://data-warehouse-connector.${process.env.REACT_APP_ENVIRONMENT}.cayuse.com/data?moduleId=SP`;
export const API_SHELL = "/api/user-details";
export const API_TENANT = "/api/tenants";
export const API_TENANT_SETTINGS = "/api/v2/tenant-settings";
export const API_USER_TASKS = "api/user-details/tasks";
export const API_SUMMARY_BASE = "/api/summary";
export const API_ACCOUNTS = "/api/accounts";
export const API_EXTENSION_SUBAWARD = "subawards";
export const API_FEED_JOBS = "/api/feed-jobs";
export const API_PERSON = "/sp/api/person";
export const API_USERS = "/sp/api/users";
export const API_BUDGET_BUILDER = "/sp/api/budget-builder";
export const API_FUNDING_PROGRAM = "/sp/api/funding-program";
export const API_CODES = "/sp/api/codes";
export const API_S2S_INTEGRATION = "/api/integration/S2S";
export const API_SP_RESEARCH_ROLES = "/sp/api/research-role";
export const API_SP_AGREEMENTS = "/api/integration/agreements";
export const API_COI_DISCLOSURE = "/sp/api/coi/disclosures";
export const API_ANIMAL_OVERSIGHT_PROTOCOLS = "/sp/api/animal-oversight/protocols";
export const API_HAZARD_SAFETY_PROTOCOLS = "/sp/api/hazard-safety/protocols";

/* Application types */
export const AWARDS = "awards";
export const PROPOSALS = "proposals";
export const SUBAWARDS = "sub-awards";
export const SUBAWARDS_ALT = "subawards";
export const PROJECTS = "projects";

/* Flowable Endpoints */
export const FLOWABLE_DESIGN_URL = process.env.REACT_APP_FLOWABLE_DESIGN_URL;
export const FLOWABLE_BASE_URL = process.env.REACT_APP_FLOWABLE_ENGAGE_URL;

export const FLOWABLE_API_CASE_INSTANCES = `${FLOWABLE_BASE_URL}/platform-api/case-instances`;
export const FLOWABLE_API_FORM_DEFINITIONS = `${FLOWABLE_BASE_URL}/form-api/form-repository/form-definitions`;

/* Administration Service Endpoints */
export const ADMINISTRATION_AD_HOC_TEAM_URL = "/api/v2/user/adhocteam";
export const ADMINISTRATION_ORGANIZATION_URL = "/api/v2/organization";
export const ADMINISTRATION_PERSON_USER_URL = "/api/v2/person/user";
export const ADMINISTRATION_PERSON_URL = "/api/v2/person";
export const ADMINISTRATION_UNIT_URL = "/api/v2/unit";
export const ADMINISTRATION_USER_URL = "/api/v2/user/user";
export const ADMINISTRATION_TENANT_URL = "/api/v2/tenant/tenant";

/* Research Suite Cache Endpoints */
export const RESEARCH_SUITE_CACHE_API = "/research-suite-cache/api";
export const RESEARCH_SUITE_CACHE_API_HUMAN_ETHICS = `${RESEARCH_SUITE_CACHE_API}/human-protocol`;

/* CayRIS API Endpoints */
export const CAYRIS_API = "/cayris-api-service/api";
export const CAYRIS_API_HUMAN_ETHICS = `${CAYRIS_API}/human-protocol`;
export const CAYRIS_API_ANIMAL_PROTOCOLS = `${CAYRIS_API}/animal-protocol`;
export const CAYRIS_API_HAZARD_SAFETY = `${CAYRIS_API}/hazard-safety-protocol`;

/* Haplo Integration Endpoints */
export const HAPLO_COSTING_API = "/api/integration/haplo/costing";
export const HAPLO_ETHICS_API = "/api/integration/haplo/ethics";
export const HAPLO_REPOSITORY_API = "/api/integration/haplo/repository";

/* Reporting source url */
export const REPORTING_SOURCE = `https://reporting-ui.${process.env.REACT_APP_ENVIRONMENT}.cayuse.com/handoff/SP`;

/* Group / Role Names */
export const ROLE_SP_SYSTEM_ADMINISTRATOR = "SP System Administrator";

export const ROLE_SP_SUBAWARD_ADMINISTRATOR = "SP Subaward Administrator";
export const ROLE_SP_SUBAWARD_EDITOR = "SP Subaward Editor";
export const ROLE_SP_SUBAWARD_VIEWER = "SP Subaward Viewer";

export const ROLE_SP_AWARD_ADMINISTRATOR = "SP Award Administrator";
export const ROLE_SP_AWARD_REVIEWER = "SP Award Reviewer";
export const ROLE_SP_AWARD_EDITOR = "SP Award Editor";
export const ROLE_SP_AWARD_VIEWER = "SP Award Viewer";

export const ROLE_SP_PROPOSAL_ADMINISTRATOR = "SP Proposal Administrator";
export const ROLE_SP_PROPOSAL_EDITOR = "SP Proposal Editor";
export const ROLE_SP_PROPOSAL_REVIEWER = "SP Proposal Reviewer";
export const ROLE_SP_PROPOSAL_VIEWER = "SP Proposal Viewer";
export const ROLE_INVENTIONS_ADMINISTRATOR = "Inventions Administrator";
export const ROLE_INVENTIONS_LICENSE_OFFICE = "Inventions License Office Agreement Manager";
export const ROLE_AGREEMENTS_USER = "Agreements User";
export const ROLE_AGREEMENTS_ADMINISTRATOR = "Agreements Administrator";
export const ROLE_AGREEMENTS_RESEARCHER = "Agreements Researcher";

export const ROLE_SP_USER = "SP User";
export const ROLE_FM_GLOBAL_ADMIN = "FM Global Admin";

export const PROJECT_SUBAWARD_ADMINISTRATOR = "SubawardAdminRole";
export const PROJECT_SUBAWARD_EDITOR = "SubawardEditorRole";
export const PROJECT_AWARD_ADMINISTRATOR = "AwardAdminRole";
export const PROJECT_AWARD_EDITOR = "AwardEditorRole";
export const PROJECT_AWARD_ADMINISTRATOR_ASSIGNED = "AwardAdminAssigned";
export const PROJECT_PROPOSAL_ADMINISTRATOR = "ProposalAdminRole";
export const PROJECT_PROPOSAL_EDITOR = "ProposalEditorRole";
export const PROJECT_PROPOSAL_ADMINISTRATOR_ASSIGNED = "ProposalAdminAssigned";
export const SUBMISSION_OWNER = "Owner";
export const RESEARCH_TEAM_MEMBER = "ResearchTeam";

//CSV injection Characters
export const INJECTION_CHARS = [ "=", "+", "-", "@" ];


export const PRODUCT_KEY = "SP";
export const FM_PRODUCT_KEY = "GRANT_FINANCIALS_DOT_NET";
export const COI_PRODUCT_KEY = "COI";
export const PROPOSALS_PRODUCT_KEY = "CAYUSE_424";
export const INVENTIONS_PRODUCT_KEY = "INVENTIONS";
export const AGREEMENTS_PRODUCT_KEY = "AGREEMENTS";
export const API_S2S_OPPORTUNITIES = "api/v1/opportunities";
export const API_S2S_PROPOSALS = "api/v1/proposals";

export const PRIVILEGES = {
	ATTACHMENT_TYPES_EDIT: "SP_SETTINGS_ACCESS",
	FORM_BUILDER_EDIT: "SP_EFORM_TEMPLATE_UPDATE",
	PROJECTS_READ: "SP_WEB_ACCESS",
	PROPOSAL_ATTACHMENTS_EDIT: "SP_PROPOSAL_UPDATE",
	PROPOSAL_ADMIN_TEAM_EDIT: "SP_PROPOSAL_ADMIN_ASSIGN",
	PROPOSAL_CREATE: "SP_WEB_ACCESS",
	PROPOSAL_DEVELOPMENT_EDIT: "SP_PROPOSAL_UPDATE",
	PROPOSAL_DEVELOPMENT_EDIT_CREATED: "SP_WEB_ACCESS",
	PROPOSAL_NOTES_READ: "SP_PROPOSAL_READ",
	PROPOSAL_NOTES_EDIT: "SP_PROPOSAL_UPDATE",
	PROPOSAL_STATUS_EDIT: "SP_PROPOSAL_STATUS_UPDATE_ALL",
	PROPOSALS_READ: "SP_PROPOSAL_READ",
	AWARD_ADMIN_TEAM_EDIT: "SP_AWARD_ADMIN_ASSIGN",
	AWARD_CREATE: "SP_AWARD_CREATE",
	AWARD_DEVELOPMENT_EDIT: "SP_AWARD_UPDATE",
	AWARD_DEVELOPMENT_EDIT_CREATED: "SP_WEB_ACCESS",
	AWARD_ATTACHMENTS_EDIT: "SP_AWARD_UPDATE",
	AWARD_EDIT: "SP_AWARD_UPDATE",
	AWARD_NOTES_READ: "SP_AWARD_READ",
	AWARD_NOTES_EDIT: "SP_AWARD_UPDATE",
	AWARD_STATUS_EDIT: "SP_AWARD_STATUS_UPDATE_ALL",
	AWARDS_READ: "SP_AWARD_READ",
	SUBAWARDS_CREATE: "SP_SUBAWARD_CREATE",
	SUBAWARDS_READ: "SP_SUBAWARD_READ",
	RESOLUTIONS_EDIT: "SP_SETTINGS_ACCESS",
	MODIFICATIONS_EDIT: "SP_SETTINGS_ACCESS",
	CERTIFICATION_TEXT_EDIT: "SP_SETTINGS_ACCESS",
	RESEARCH_TEAM_ROLES_EDIT: "SP_SETTINGS_ACCESS",
};

/* Modification status*/
export const MODIFICATION_COMPLETED = "COMPLETED";
export const MODIFICATION_DRAFT = "DRAFT";

/* Local & Session Storage Keys */
export const STORAGE_AWARD_ACTIVE_COLS =  "awards:activeColumns";
export const STORAGE_AWARDS_CONTENT = "awards:content";
export const STORAGE_MODIFICATIONS_ACTIVE_COLS =  "modifications:activeColumns";
export const STORAGE_SUBAWARDS_MODIFICATIONS_ACTIVE_COLS =  "subawardsModifications:activeColumns";
export const STORAGE_FACETED_SEARCH = "facetedSearchBar:searchQuery";
export const STORAGE_TASK_ACTIVE_COLS = "tasks:activeColumns";
export const STORAGE_PROJECT_ACTIVE_COLS = "projects:activeColumns";
export const STORAGE_PROPOSAL_ACTIVE_COLS = "proposals:activeColumns";
export const STORAGE_PROPOSAL_REPORT_COLS = "proposals:reportColumns";
export const STORAGE_AWARD_REPORT_COLS = "awards:reportColumns";
export const STORAGE_PROPOSALS_CONTENT = "proposals:content";
export const STORAGE_SHELL_CONTENT = "shell:content";
export const STORAGE_SUBAWARDS_CONTENT =  "subawards:content";
export const STORAGE_SUBAWARD_ACTIVE_COLS =  "subawards:activeColumns";
export const STORAGE_FORM_STATUS = "proposalFormStatuses";
export const STORAGE_FEED_JOBS_JOB_ID = "feed-jobs:jobId";
export const STORAGE_PROJECT_TASKS_TABLE_SORTING = "tasksTableSorting";
export const STORAGE_AGREEMENTS_TABLE_SORTING = "agreemnetsTableSorting";

/* Limits */
export const MAX_ACTIVE_COLUMN_HEADERS = 11;
export const MAX_FILE_SIZE = 10485760;
export const MAX_RETRY_NUMBER = 3;

/* Reports */
export const REPORT_DATASOURCE_TYPE = "json";
export const REPORT_GRID_TYPE = "flat";
export const REPORT_DATE_FIELDS = [ "created-date", "sponsor-deadline" ];

// Pendo
export const PENDO_PRODUCT_NAME = "Cayuse Sponsored Projects";
export const PENDO_API_KEY = "f904247c-41e5-43cf-7c8c-4c8f5b99f81f";

// Zendesk
export const ZENDESK_KEY = IS_FT || IS_ENG ? "d2ff2523-3364-4e03-b660-6dff652c61d6" :
	(process.env.REACT_APP_ZENDESK_KEY || "bca4b06c-ddbb-44ff-b339-599262dfba01");

export const FEED_JOBS_STATUSES = {
	PENDING: "PENDING",
	PROCESSING: "PROCESSING",
	FINISHED: "FINISHED",
	FAILED: "FAILED",
	CANCELED: "CANCELED",
};

/* Key Codes */
export const KEY_CODES = {
	TAB: 9,
	ENTER: 13,
	ESCAPE: 27,
	LEFT_ARROW: 37,
	UP_ARROW: 38,
	RIGHT_ARROW: 39,
	DOWN_ARROW: 40
};

/* Response status codes */
export const ERROR_STATUS_CODE_200 = 200;

/* Error status codes */
export const ERROR_STATUS_CODE_400 = 400;
export const ERROR_STATUS_CODE_401 = 401;
export const ERROR_STATUS_CODE_403 = 403;
export const ERROR_STATUS_CODE_404 = 404;
export const ERROR_STATUS_CODE_406 = 406;
export const ERROR_STATUS_CODE_409 = 409;
export const ERROR_STATUS_CODE_410 = 410;
export const ERROR_STATUS_CODE_413 = 413;
export const ERROR_STATUS_CODE_422 = 422;
export const ERROR_STATUS_CODE_500 = 500;
export const ERROR_FORBIDDEN_FILE_EXTENSION = "FORBIDDEN_FILE_EXTENSION";
export const ERROR_UPDATE_REJECTED = "UPDATE_REJECTED";

/* Error responses */
export const ERROR_RESPONSE_SERVICE_UNAVAILABLE = "Service Unavailable";

/* Error messages */
export const ERROR_NOT_AUTHORIZED = "The action is not authorized. Please contact an administrator to check your permissions.";
export const ERROR_INTERNAL_SERVER_ERROR = "Internal server error";
export const ERROR_UNABLE_DOWNLOAD_FILE = "Unable to download the requested file";

/* Misc. Keys */
export const DEFAULT_DATE_FORMAT = "l";
export const DEFAULT_DATETIME_FORMAT = "l h:mm:ss a";
export const DEFAULT_CURRENCY = "USD";
export const DEFAULT_LOCALE = "en-US";
export const FORM_OUTCOME_COMPLETE = "__COMPLETE";
export const NEW_RULE_ID = "__add__";
export const SELECT_ALL_KEY = "__all__";
export const SELECT_ANY_KEY = "*";
export const WEEK_IN_MILLISECONDS = 604800000;
export const DAY_IN_MILLISECONDS = 86400000;
export const MAX_TASKS_SHOWED = 15;
export const TRANSITION_DURATION = 150;

export const RULE_TYPES = {
	AD_HOC_NOTIFICATION: "AD_HOC_NOTIFICATION",
	AD_HOC_REVIEW: "AD_HOC_REVIEW",
	DISPLAY_RULES: "DISPLAY_RULES",
	RT_CERTIFICATION: "RT_CERTIFICATION",
	RT_REVIEW: "RT_REVIEW",
	UNIT_REVIEW: "UNIT_REVIEW",
	INFOREADY_REVIEW: "INFOREADY_REVIEW",
	DISCLOSURE: "DISCLOSURE",
	NOTIFICATION: "NOTIFICATION",
};

export const RULE_SUBTYPES = {
	UNIT : "UNIT",
	SPECIFIC_UNIT : "SPECIFIC_UNIT",
	RT : "RT",
	RT_MEMBERS: "RT_MEMBERS",
};

export const ACTION_TYPES = {
	REVIEW: "review",
	NOTIFICATION: "notification",
	CERTIFICATION: "certification",
	INTEGRATION: "integration",
};

//TODO: Replace RULE_TYPES with the NEW_RULE_TYPES
export const NEW_RULE_TYPES = {
	ANSWERED: "ANSWERED",
	RT_ROLE: "RT_ROLE",
	EXACT: "EXACT"
};

export const RULE_STRUCTURE_TYPES = {
	GROUP: "GROUP",
	SINGLE: "SINGLE"
};

/* Field / Column Definitions */
export const FIELD_TYPES = {
	DATE: "date",
	DATETIME: "datetime",
	NUMBER: "number",
	RADIO: "radio",
	RESEARCH_TEAM: "researchTeam",
	RICH_TEXT: "richText",
	SELECT: "select",
	STRING_LIST: "stringList",
	SUBFORM: "subform",
	TEXT: "text",
	TEXTAREA: "textarea",
	UPLOAD: "fileInput",
	CURRENCY: "currency",
	CAYUSE_CHECKBOXES: "cayuseCheckboxes",
	ONE: "one",
	DATE_STRING: "date string",
	CLASSIFICATION_CODES: "classificationCodes",
	FUNDING_PROGRAM: "fundingProgram",
	CHECKBOX: "boolean",
	BUDGET_BUILDER: "cayuseSummaryBudgetImporter",
	DATA_TABLE: "dataTable",
};

export const CAYUSE_TYPES = {
	ORG_FINDER: "org-finder",
	PERSON_FINDER: "person-finder",
	UNIT_FINDER: "unit-finder",
	RESEARCH_TEAM: "research-team",
	FUNDING_PROGRAM: "funding-program",
	CLASSIFICATION_CODES: "classification-codes",
	HAZARD_SAFETY: "cayuse-hazard-safety",
	HUMAN_ETHICS: "cayuse-human-ethics-study-search",
	ANIMAL_OVERSIGHT: "cayuse-animal-oversight",
	HAPLO_COSTING: "cayuse-haplo-costing-tool",
	HAPLO_ETHICS: "cayuse-haplo-ethics-monitor",
	HAPLO_REPOSITORY: "cayuse-haplo-repository",
	CAYUSE_SUMMARY_BUDGET_IMPORTER: "cayuse-summary-budget-importer",
	PROPOSALS_S2S: "cayuse-proposals-s2s-record",
};

export const CAYUSE_SUB_TYPES = {
	PROGRAM_ROUND_INTERNAL_CLOSING_DATE: "FUNDING_PROGRAM_ROUND_INTERNAL_CLOSING_DATE",
	PROGRAM_ROUND_EXTERNAL_CLOSING_DATE: "FUNDING_PROGRAM_ROUND_EXTERNAL_CLOSING_DATE",
};

const addIdsToColumnHeaders = columnHeaders => {
	const columnHeadersWithIds = {};

	for (const key in columnHeaders) {
		if (columnHeaders.hasOwnProperty(key)) {
			const header = columnHeaders[key];
			columnHeadersWithIds[key] = {
				...header,
				id: slugifyString(header.columnHeader, {
					substitutions: [
						[ "#", "number" ]
					]
				})
			};
		}
	}

	return columnHeadersWithIds;
};

export const DEFAULT_COLUMNS_KEY = {
	PI: "principalInvestigator",
	ADMIN_UNIT: "adminUnit",
	SPONSOR: "sponsor",
	PRIME_SPONSOR: "primeSponsor",
};

export const DEFAULT_AWARD_COLUMN_HEADERS = (() => {
	const defaultColumnHeaders = {
		PI: { keys: [ "principal-investigator" ], specialKey: "principalInvestigator", columnHeader: "PI", type: FIELD_TYPES.TEXT, cayuseType: null },
		PI_UNIT: { keys: [ "principalInvestigatorUnit" ], specialKey: "principalInvestigatorUnit", columnHeader: "PI Unit", type: FIELD_TYPES.TEXT, cayuseType: null },
		ADMIN_UNIT: { keys: [], specialKey: "adminUnit", columnHeader: "Admin Unit", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.UNIT_FINDER },
		ADMIN_ASSIGNED: { keys: [ "adminTeamMembers" ], columnHeader: "Admin Assigned", type: FIELD_TYPES.SELECT, cayuseType: null },
		ANTICIPATED_AMOUNT: { keys: [ "anticipated-amount" ], columnHeader: "Anticipated Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		SPONSOR: { keys: [], columnHeader: "Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.ORG_FINDER },
		PRIME_SPONSOR: { keys: [], specialKey: "primeSponsor", columnHeader: "Prime Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.PERSON_FINDER },
		SPONSOR_AWARD_NUMBER: { keys: [], columnHeader: "Sponsor Award Number", type: FIELD_TYPES.NUMBER, cayuseType: null }, //todo: confirm type
		AWARD_TITLE: { keys: [], columnHeader: "Award Title", type: FIELD_TYPES.TEXT, cayuseType: null },
		AWARD_AMOUNT: { keys: [], columnHeader: "Award Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		AWARD_START_DATE: { keys: [], columnHeader: "Award Start Date", type: FIELD_TYPES.DATE, cayuseType: null },
		AWARD_END_DATE: { keys: [], columnHeader: "Award End Date", type: FIELD_TYPES.DATE, cayuseType: null },
		AWARD_NOTICE_RECEIVED: { keys: [], columnHeader: "Award Notice Received", type: FIELD_TYPES.DATE, cayuseType: null },
		INSTRUMENT_TYPE: { keys: [], columnHeader: "Instrument Type", type: FIELD_TYPES.SELECT, cayuseType: null },
		AWARD_ID: { keys: [], columnHeader: "Award Id", type: FIELD_TYPES.TEXT, cayuseType: null, hiddenFromUI: true },
		AWARD_NUMBER: { keys: [], columnHeader: "Award #", type: FIELD_TYPES.TEXT, cayuseType: null, alwaysActive: true },
		PROJECT_TITLE: { keys: [], specialKey: "projectTitle", columnHeader: "Project Title", type: FIELD_TYPES.TEXT, cayuseType: null },
		PROJECT_NUMBER: { keys: [], specialKey: "projectNumber", columnHeader: "Project #", type: FIELD_TYPES.TEXT, cayuseType: null }, //todo: project number is here to fill in BE CSV defaults, might have to delete
		OBLIGATED_AMOUNT: { keys: [], columnHeader: "Obligated Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		STATUS: { keys: [], columnHeader: "Status", type: FIELD_TYPES.TEXT, cayuseType: null },
		TOTAL_EXPECTED_AMOUNT: { keys: [], columnHeader: "Total Expected Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		CREATED_DATE: {
			keys: [ "created-on" ],
			specialKey: "createdOn",
			columnHeader: "Created Date",
			type: FIELD_TYPES.DATETIME,
			cayuseType: null,
			extraSettings: {
				enableTime: true
			},
		},
		CREATED_BY: {
			keys: [ "created-by" ],
			specialKey: "createdByName",
			columnHeader: "Created By",
			type: FIELD_TYPES.TEXT,
			cayuseType: null
		},
		MODIFICATION_NUMBER: { keys: [ "modification-number", "modificationNumber" ], specialKey: "modificationNumber", columnHeader: "Modification Number", type: FIELD_TYPES.NUMBER, cayuseType: null },
		MODIFICATION_TYPE: { keys: [ "modification-type", "modificationType" ], specialKey: "modificationType", columnHeader: "Modification Type", type: FIELD_TYPES.TEXT, cayuseType: null },
		MODIFICATION_DATE: { keys: [ "modification-date", "modificationDate" ], specialKey: "modificationCreateDate", columnHeader: "Modification Date", type: FIELD_TYPES.DATE, cayuseType: null },
		MODIFIED_BY: { keys: [ "modified-by", "modifiedBy" ], specialKey: "modifiedBy", columnHeader: "Modified By", type: FIELD_TYPES.TEXT, cayuseType: null },
		AWARD_TYPE: { keys: [ "award-type" ], specialKey: "awardType", columnHeader: "Award Type", type: FIELD_TYPES.TEXT, cayuseType: null },
		INCREMENT_AMOUNT: { keys: [ "increment-amount" ], specialKey: "incrementAmount", columnHeader: "Increment Amount", type: FIELD_TYPES.TEXT, cayuseType: null },
		HISTORY_ACTION: { keys: [], columnHeader: "History Action", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		HISTORY_ACTION_BY: { keys: [], columnHeader: "History Action By", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		HISTORY_ACTION_DATE: { keys: [], columnHeader: "History Action Date", type: FIELD_TYPES.DATETIME, cayuseType: null, reportOnly: true },
		HISTORY_COMMENT: { keys: [], columnHeader: "History Comment", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_REVIEW_ORDER: { keys: [], columnHeader: "Routing Review Order", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_TEAM: { keys: [], columnHeader: "Routing Team", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_MEMBERS: { keys: [], columnHeader: "Routing Members", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_STATUS: { keys: [], columnHeader: "Routing Status", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_DATE: { keys: [], columnHeader: "Routing Date", type: FIELD_TYPES.DATETIME, cayuseType: null, reportOnly: true },
		ROUTING_COMMENT: { keys: [], columnHeader: "Routing Comment", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true }
	};

	return addIdsToColumnHeaders(defaultColumnHeaders);
})();

export const DEFAULT_SUBAWARD_COLUMN_HEADERS = (() => {
	const defaultColumnHeaders = {
		ADMIN_UNIT: { keys: [], specialKey: "adminUnit", columnHeader: "Admin Unit", type: "select" },
		DATE_EXECUTED: { keys: [], columnHeader: "Date Executed", type: FIELD_TYPES.TEXT, cayuseType: null },
		DATE_RECEIVED: { keys: [], columnHeader: "Date Received", type: FIELD_TYPES.DATE, cayuseType: null },
		DATE_SENT: { keys: [], columnHeader: "Date Sent", type: FIELD_TYPES.DATE, cayuseType: null },
		PRIMARY_ADMIN_CONTACT: { keys: [], columnHeader: "Primary Admin contact", type: FIELD_TYPES.TEXT, cayuseType: null },
		PRIMARY_CONTACT_EMAIL: { keys: [], columnHeader: "Primary Contact email", type: "text", cayuseType: null },
		RESOLUTION: { keys: [ "resolution" ], columnHeader: "Resolution", type: "text", cayuseType: null },
		PRIME_SPONSOR: { keys: [], specialKey: "primarySponsor", columnHeader: "Prime Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.PERSON_FINDER },
		PI: { keys: [ "principal-investigator" ], specialKey: "principalInvestigator", columnHeader: "PI", type: FIELD_TYPES.TEXT, cayuseType: null },
		PROJECT_TITLE: { keys: [], columnHeader: "Project Title", type: "text" },
		PROJECT_NUMBER: { keys: [], columnHeader: "Project #", type: "text" },
		SPONSOR: { keys: [], specialKey: "sponsor", columnHeader: "Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.ORG_FINDER },
		STATUS: { keys: [], columnHeader: "Status", type: "select", cayuseType: null },
		TOTAL_EXPECTED_AMOUNT: { keys: [], columnHeader: "Total Expected Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		SUBAWARD_BEGIN_DATE: { keys: [], columnHeader: "Subaward Begin Date", type: FIELD_TYPES.DATE, cayuseType: null },
		SUBAWARD_END_DATE: { keys: [], columnHeader: "Subaward End Date", type: FIELD_TYPES.DATE, cayuseType: null },
		SUBAWARD_ID: { keys: [], columnHeader: "Subaward Id", type: "text", cayuseType: null, hiddenFromUI: true },
		SUBAWARD_NUMBER: { keys: [ "subawardNumber" ], columnHeader: "Subaward #", type: FIELD_TYPES.TEXT, cayuseType: null, alwaysActive: true },
		AWARD_NUMBER: { keys: [ "awardNumber" ], specialKey: "awardNumber", columnHeader: "Award #", type: FIELD_TYPES.TEXT, cayuseType: null },
		SUBAWARD_TITLE: { keys: [], columnHeader: "Title", type: "text", cayuseType: null },
		SUBAWARDEE: { keys: [], columnHeader: "Subawardee", type: FIELD_TYPES.TEXT, cayuseType: null },
		TOTAL_EXPECTED: { keys: [], columnHeader: "Total Expected Amount", type: FIELD_TYPES.CURRENCY, cayuseType: null },
		TOTAL_OBLIGATED: { keys: [], columnHeader: "Total Obligated", type: FIELD_TYPES.CURRENCY, cayuseType: null },
		CREATED_DATE: {
			keys: [ "created-on", "createdOn" ],
			specialKey: "createdOn",
			columnHeader: "Created Date",
			type: FIELD_TYPES.DATETIME,
			cayuseType: null,
			extraSettings: {
				enableTime: true
			},
		},
		CREATED_BY: {
			keys: [ "created-by", "createdByName" ],
			specialKey: "createdByName",
			columnHeader: "Created By",
			type: FIELD_TYPES.TEXT,
			cayuseType: null
		},
		MODIFICATION_NUMBER: { keys: [ "modification-number", "modificationNumber" ], specialKey: "modificationNumber", columnHeader: "Modification Number", type: FIELD_TYPES.NUMBER, cayuseType: null },
		MODIFICATION_TYPE: { keys: [ "modification-type", "modificationType" ], specialKey: "modificationType", columnHeader: "Modification Type", type: FIELD_TYPES.TEXT, cayuseType: null },
		MODIFICATION_DATE: { keys: [ "modification-date", "modificationDate" ], specialKey: "modificationCreateDate", columnHeader: "Modification Date", type: FIELD_TYPES.DATE, cayuseType: null },
		MODIFIED_BY: { keys: [ "modified-by", "modifiedBy" ], specialKey: "modifiedBy", columnHeader: "Modified By", type: FIELD_TYPES.TEXT, cayuseType: null },
	};

	return addIdsToColumnHeaders(defaultColumnHeaders);
})();

export const DEFAULT_PROPOSAL_COLUMN_HEADERS = (() => {
	const defaultColumnHeaders = {
		ADMIN_UNIT: { keys: [], specialKey: "adminUnit", columnHeader: "Admin Unit", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.UNIT_FINDER },
		INSTRUMENT_TYPE: { keys: [], columnHeader: "Instrument Type", type: FIELD_TYPES.SELECT, cayuseType: null },
		PI: { keys: [ "principal-investigator" ], specialKey: "principalInvestigator", columnHeader: "PI", type: FIELD_TYPES.TEXT, cayuseType: null },
		PI_UNIT: { keys: [ "principalInvestigatorUnit" ], specialKey: "principalInvestigatorUnit", columnHeader: "PI Unit", type: FIELD_TYPES.TEXT, cayuseType: null },
		PRIME_SPONSOR: { keys: [], specialKey: "primeSponsor", columnHeader: "Prime Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.PERSON_FINDER },
		PROJECT_END_DATE: { keys: [], columnHeader: "Project End Date", type: FIELD_TYPES.DATE, cayuseType: null },
		PROJECT_NUMBER: { keys: [], specialKey: "projectNumber", columnHeader: "Project #", type: FIELD_TYPES.TEXT, cayuseType: null },
		PROJECT_START_DATE: { keys: [], columnHeader: "Project Start Date", type: FIELD_TYPES.DATE, cayuseType: null },
		PROJECT_TITLE: { keys: [], specialKey: "projectTitle", columnHeader: "Project Title", type: FIELD_TYPES.TEXT, cayuseType: null },
		CREATED_DATE: {
			keys: [ "created-on" ],
			specialKey: "createdOn",
			columnHeader: "Created Date",
			type: FIELD_TYPES.DATETIME,
			cayuseType: null,
			extraSettings: {
				enableTime: true
			},
		},
		CREATED_BY: {
			keys: [ "created-by" ],
			specialKey: "createdByName",
			columnHeader: "Created By",
			type: FIELD_TYPES.TEXT,
			cayuseType: null
		},
		PROPOSAL_NUMBER: { keys: [], specialKey: "proposalNumber", columnHeader: "Proposal #", type: FIELD_TYPES.TEXT, cayuseType: null, alwaysActive: true },
		PROPOSAL_ID: { keys: [], columnHeader: "Proposal ID", type: FIELD_TYPES.TEXT, cayuseType: null, hiddenFromUI: true },
		PROPOSAL_TYPE: { keys: [], columnHeader: "Proposal Type", type: FIELD_TYPES.SELECT, cayuseType: null },
		PROPOSED_TOTAL_AMOUNT: { keys: [], columnHeader: "Proposed Total Amount", type: FIELD_TYPES.NUMBER, cayuseType: null },
		TOTAL_SPONSOR_COSTS: { keys: [], columnHeader: "Total Sponsor Costs", type: FIELD_TYPES.NUMBER, cayuseType: null },
		SPONSOR: { keys: [], columnHeader: "Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.ORG_FINDER },
		SPONSOR_DEADLINE: {
			keys: [],
			columnHeader: "Sponsor Deadline",
			type: FIELD_TYPES.DATETIME,
			cayuseType: null,
			extraSettings: {
				enableTime: true
			}
		},
		STATUS: { keys: [], columnHeader: "Status", type: FIELD_TYPES.TEXT, cayuseType: null },
		HISTORY_ACTION: { keys: [], columnHeader: "History Action", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		HISTORY_ACTION_BY: { keys: [], columnHeader: "History Action By", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		HISTORY_ACTION_DATE: { keys: [], columnHeader: "History Action Date", type: FIELD_TYPES.DATETIME, cayuseType: null, reportOnly: true },
		HISTORY_COMMENT: { keys: [], columnHeader: "History Comment", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_REVIEW_ORDER: { keys: [], columnHeader: "Routing Review Order", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_TEAM: { keys: [], columnHeader: "Routing Team", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_MEMBERS: { keys: [], columnHeader: "Routing Members", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_STATUS: { keys: [], columnHeader: "Routing Status", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true },
		ROUTING_DATE: { keys: [], columnHeader: "Routing Date", type: FIELD_TYPES.DATETIME, cayuseType: null, reportOnly: true },
		ROUTING_COMMENT: { keys: [], columnHeader: "Routing Comment", type: FIELD_TYPES.TEXT, cayuseType: null, reportOnly: true }
	};

	return addIdsToColumnHeaders(defaultColumnHeaders);
})();

export const DEFAULT_BUDGET_COLUMN_HEADERS = (() => {
	const defaultColumnHeaders = {
		PN: { keys: [ "proposalNumber" ], columnHeader: "Proposal Number", type: FIELD_TYPES.TEXT, cayuseType: null },
		PERIODS: { keys: [ "F&A" ], columnHeader: "Periods", type: FIELD_TYPES.TEXT, cayuseType: null },
		DIRECT_COSTS: { keys: [ "other-direct-costs" ], columnHeader: "Direct Costs", type: FIELD_TYPES.TEXT, cayuseType: null },
		FA_COST: { keys: [ "fa-cost" ], columnHeader: "F&A Cost", type: FIELD_TYPES.TEXT, cayuseType: null },
		COST_SHARE: { keys: [ "cost-share" ], columnHeader: "Cost Share", type: FIELD_TYPES.TEXT, cayuseType: null },
		ADMIN_UNIT: { keys: [], specialKey: "adminUnit", columnHeader: "Admin Unit", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.UNIT_FINDER },
		INSTRUMENT_TYPE: { keys: [], columnHeader: "Instrument Type", type: FIELD_TYPES.SELECT, cayuseType: null },
		PI: { keys: [ "principal-investigator" ], specialKey: "principalInvestigator", columnHeader: "PI", type: FIELD_TYPES.TEXT, cayuseType: null },
		PRIME_SPONSOR: { keys: [], specialKey: "primeSponsor", columnHeader: "Prime Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.PERSON_FINDER },
		PROJECT_END_DATE: { keys: [], columnHeader: "Project End Date", type: FIELD_TYPES.DATE, cayuseType: null },
		PROJECT_NUMBER: { keys: [], columnHeader: "Project #", type: FIELD_TYPES.TEXT, cayuseType: null },
		PROJECT_START_DATE: { keys: [], columnHeader: "Project Start Date", type: FIELD_TYPES.DATE, cayuseType: null },
		PROJECT_TITLE: { keys: [], columnHeader: "Project Title", type: FIELD_TYPES.TEXT, cayuseType: null },
		PROPOSAL_NUMBER: { keys: [], columnHeader: "Proposal #", type: FIELD_TYPES.TEXT, cayuseType: null, alwaysActive: true },
		PROPOSAL_ID: { keys: [], columnHeader: "Proposal ID", type: FIELD_TYPES.TEXT, cayuseType: null, hiddenFromUI: true },
		PROPOSAL_TYPE: { keys: [], columnHeader: "Proposal Type", type: FIELD_TYPES.SELECT, cayuseType: null },
		PROPOSED_TOTAL_AMOUNT: { keys: [], columnHeader: "Proposed Cost", type: FIELD_TYPES.NUMBER, cayuseType: null },
		PROJECT_TOTAL_AMOUNT: { keys: [], columnHeader: "Total Project Cost", type: FIELD_TYPES.NUMBER, cayuseType: null },
		SPONSOR: { keys: [], specialKey: "sponsor", columnHeader: "Sponsor", type: FIELD_TYPES.SELECT, cayuseType: CAYUSE_TYPES.ORG_FINDER },
		SPONSOR_DEADLINE: {
			keys: [],
			columnHeader: "Sponsor Deadline",
			type: FIELD_TYPES.DATETIME,
			cayuseType: null,
			extraSettings: {
				enableTime: true
			}
		},
		STATUS: { keys: [], columnHeader: "Status", type: FIELD_TYPES.TEXT, cayuseType: null },
	};

	return addIdsToColumnHeaders(defaultColumnHeaders);
})();

export const DEFAULT_MODIFICATION_COLUMN_HEADERS = [
	{ keys: [ "formNumber" ], columnHeader: "Modification", slug: "formNumber", id: "form-number" },
	{ keys: [ "category" ], columnHeader: "Modification Type", slug: "category", id: "modification-type" },
	{ keys: [ "modificationDate" ], columnHeader: "Modification Date", slug: "modificationDate", id: "modified", type: FIELD_TYPES.DATE },
	{ keys: [ "modifiedByName" ], columnHeader: "Modified By", slug: "modifiedByName", id: "modified-by" },
	{ keys: [ "comment" ], columnHeader: "Comment", slug: "comments", id: "comment" }
];

export const DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS = [
	{ keys: [ "formNumber" ], columnHeader: "Modification", slug: "formNumber", id: "form-number", disabled: true, alwaysActive: true },
	{ keys: [ "category" ], columnHeader: "Modification Type", slug: "category", id: "modification-type" },
	{ keys: [ "modificationDate" ], columnHeader: "Modification Date", slug: "modificationDate", id: "modified", type: FIELD_TYPES.DATE },
	{ keys: [ "modifiedByName" ], columnHeader: "Modified By", slug: "modifiedByName", id: "modified-by" },
	{ keys: [ "comment" ], columnHeader: "Comment", slug: "comments", id: "comment" }
];

export const PROPOSAL_LIST_DEFAULT_COLUMNS = [
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROPOSAL_NUMBER,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PI,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PI_UNIT,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.STATUS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_START_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_END_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROPOSAL_TYPE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.INSTRUMENT_TYPE
];

export const AWARDS_LIST_DEFAULT_COLUMNS = [
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.PI,
	DEFAULT_AWARD_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR_AWARD_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.INSTRUMENT_TYPE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_START_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_END_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_AWARD_COLUMN_HEADERS.OBLIGATED_AMOUNT
];

export const SUBAWARDS_LIST_DEFAULT_COLUMNS = [
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_NUMBER,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.STATUS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARDEE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_BEGIN_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_END_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.TOTAL_EXPECTED,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_ADMIN_CONTACT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_CONTACT_EMAIL,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.RESOLUTION,
];

export const PROJECT_MAIN_VIEW_LIST_DEFAULT_COLUMNS = [
	{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_TITLE, keys: [ "title" ] },
	{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_NUMBER, keys: [ "projectNumber" ] },
	{ ...DEFAULT_PROPOSAL_COLUMN_HEADERS.STATUS, keys: [ "status" ] },
];

export const PROPOSAL_MAIN_VIEW_LIST_DEFAULT_COLUMNS = [
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROPOSAL_NUMBER,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PI,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.STATUS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_START_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_END_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROPOSAL_TYPE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.INSTRUMENT_TYPE
];

export const PROPOSAL_REPORT_LIST_DEFAULT_COLUMNS = [
	...PROPOSAL_LIST_DEFAULT_COLUMNS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_NUMBER,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.CREATED_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.TOTAL_SPONSOR_COSTS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.SPONSOR_DEADLINE,
];

export const AWARDS_REPORT_LIST_DEFAULT_COLUMNS = [
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.PI,
	DEFAULT_AWARD_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_AWARD_COLUMN_HEADERS.SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_AWARD_COLUMN_HEADERS.INSTRUMENT_TYPE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_START_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_END_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_AWARD_COLUMN_HEADERS.OBLIGATED_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.PROJECT_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.TOTAL_EXPECTED_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.ANTICIPATED_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.STATUS,
	DEFAULT_AWARD_COLUMN_HEADERS.CREATED_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.AWARD_TYPE,
	DEFAULT_AWARD_COLUMN_HEADERS.INCREMENT_AMOUNT,
	DEFAULT_AWARD_COLUMN_HEADERS.PI_UNIT,
	DEFAULT_AWARD_COLUMN_HEADERS.MODIFICATION_NUMBER,
	DEFAULT_AWARD_COLUMN_HEADERS.MODIFICATION_TYPE,
	DEFAULT_AWARD_COLUMN_HEADERS.MODIFICATION_DATE,
	DEFAULT_AWARD_COLUMN_HEADERS.MODIFIED_BY,
];

export const SUBAWARDS_REPORT_LIST_DEFAULT_COLUMNS = [
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_NUMBER,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PROJECT_TITLE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.STATUS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARDEE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_BEGIN_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_END_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.TOTAL_EXPECTED_AMOUNT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_ADMIN_CONTACT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.RESOLUTION,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.MODIFICATION_NUMBER,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.MODIFICATION_TYPE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.MODIFICATION_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.MODIFIED_BY,
];

export const START_AWARD_FROM_PROPOSAL = {
	requiredFields: [
		"proposalId",
		"proposalNumber",
		"projectTitle",
		"principalInvestigator",
	],

	searchFields: [
		"proposalNumber",
		"projectTitle",
		"principalInvestigator",
	]
};

export const START_AWARD_FROM_AWARD = {
	requiredFields: [
		"awardId",
		"awardNumber",
		"projectTitle",
		"principalInvestigator",
	],
	searchFields: [
		"awardNumber",
		"projectTitle",
		"principalInvestigator",
	]
};

export const START_PROPOSAL_FROM_PROJECT = {
	requiredFields: [
		"projectId",
		"projectNumber",
		"title",
	],

	searchFields: [
		"projectNumber",
		"title",
	]
};


export const BUDGET_FILE_HEADERS = {
	directCosts: [
		"research_team",
		"equipment",
		"travel",
		"participant_support",
		"other_direct"
	],
	sponsorCosts: [
		"F&A"
	],
	costShare: [
		"internal_share",
		"external_share"
	],
	displayMap: {
		research_team: "Research Team",	// eslint-disable-line camelcase
		equipment: "Equipment",
		travel: "Travel",
		participant_support: "Participant Support Costs",	// eslint-disable-line camelcase
		other_direct: "Other Direct Costs",	// eslint-disable-line camelcase
		"F&A": "F&A",
		internal_share: "Internal Cost Share",	// eslint-disable-line camelcase
		external_share: "External Cost Share"	// eslint-disable-line camelcase
	}
};

export const BUDGET_ROWS = {
	total_direct_costs: { value: "Total Direct Costs", type: "subTotal" }, // eslint-disable-line camelcase
	"F&A": { value: "F&A", type: "expense" },
	total_sponsor_costs: { value: "Total Sponsor Costs", type: "subTotal" }, // eslint-disable-line camelcase
	internal_share: { value: "Internal Cost Share", type: "expense" },	// eslint-disable-line camelcase
	external_share: { value: "External Cost Share", type: "expense" },	// eslint-disable-line camelcase
	total_cost_share: { value: "Total Cost Share", type: "subTotal" }, // eslint-disable-line camelcase
	total_project_cost: { value: "Total Project Cost", type: "total" } // eslint-disable-line camelcase
};

export const FORM_ACCESS_LEVELS = {
	PROPOSAL: {
		ADMIN: [
			"Editor",
			SUBMISSION_OWNER,
			RESEARCH_TEAM_MEMBER,
			PROJECT_PROPOSAL_ADMINISTRATOR,
			PROJECT_PROPOSAL_ADMINISTRATOR_ASSIGNED,
			ROLE_SP_PROPOSAL_ADMINISTRATOR,
			ROLE_SP_SYSTEM_ADMINISTRATOR,
		],
		EDITOR: [ PROJECT_PROPOSAL_EDITOR ],
		REVIEWER: [
			"Unit",
			"AdhocTeam",
		],
		VIEWER: [ "Viewer" ]
	},
	 BUDGET: {
		ADMIN: [
			"Editor",
			SUBMISSION_OWNER,
			RESEARCH_TEAM_MEMBER,
			ROLE_SP_PROPOSAL_ADMINISTRATOR,
			ROLE_SP_SYSTEM_ADMINISTRATOR,
		],
		REVIEWER: [
			"Unit",
		],
		VIEWER: [ "Viewer" ]
	},
	AWARD: {
		ADMIN: [
			"Editor",
			SUBMISSION_OWNER,
			PROJECT_AWARD_ADMINISTRATOR,
			PROJECT_AWARD_ADMINISTRATOR_ASSIGNED,
			ROLE_SP_AWARD_ADMINISTRATOR,
			ROLE_SP_SYSTEM_ADMINISTRATOR,
		],
		EDITOR: [ PROJECT_AWARD_EDITOR ],
		REVIEWER: [
			"Unit",
			"ResearchTeam",
			"AdhocTeam",
		],
		VIEWER: [
			"Viewer"
		]
	},
	SUBAWARD: {
		ADMIN: [
			"Editor",
			SUBMISSION_OWNER,
			PROJECT_SUBAWARD_ADMINISTRATOR,
			ROLE_SP_SUBAWARD_ADMINISTRATOR,
			ROLE_SP_SYSTEM_ADMINISTRATOR,
		],
		EDITOR: [ PROJECT_SUBAWARD_EDITOR ],
		REVIEWER: [
			"Unit",
			"ResearchTeam",
			"AdhocTeam",
		],
		VIEWER: [
			"Viewer"
		]
	}
};

export const FORM_MANAGERS = [
	"cayuseadmin"
];

export const AWARD_REVIEW_STATUS = {
	active: "Active",
	closed: "Closed",
	closeout: "In Closeout",
	deferred: "DEFERRED",
	inReview: "In Review & Negotiation",
	notStarted: "NOT_STARTED",
	setup: "Set Up In Progress",
	started: "STARTED",
};

export const SUBAWARD_REVIEW_STATUS = {
	active: "Active",
	closed: "Closed",
	closeout: "In Closeout",
	inReview: "In Review & Negotiation",
	setup: "Set Up In Progress",
};

export const PROPOSAL_REVIEW_STATUS = {
	inDevelopment: "In Development",
	underReview: "Under Review",
	approved: "Approved",
	submittedToSponsor: "Submitted to Sponsor",
	underConsideration: "Under Consideration",
	funded: "Funded",
	closed: "Closed",
};

export const MODIFICATION_STATUS = {
	active: "Active",
	closeout: "In Closeout",
	draft: "DRAFT",
};

export const STATUS_OPTIONS = [
	{
		text:"Active",
		value: "Active",
		key: "active"
	},
	{
		text: "On Hold",
		value: "On Hold",
		key: "on-hold"
	},
	{
		text: "Closed",
		value: "Closed",
		key: "closed"
	}
];

export const FORM_SUBMISSION_TYPES = {
	AWARD: "award",
	SUBAWARD: "subaward",
	PROPOSAL: "proposal",
	PROJECT: "project",
};

export const AFB_UNUSABLE_ELEMENTS = [
	"Text List",
	"Password",
	"IRB Study",
	"Outcome Button",
];

export const RT_OBJECT_DEFAULT_VALUE_KEY = "listTheProjectTeamMembersStartingWithThePrincipalInvestigator";
export const RT_OBJECT_DEFAULT_MISSING_COUNT = 3;
export const RT_OBJECT_ASSOCIATION_DEFAULT_MISSING_COUNT = 1;

export const FUNDING_PROGRAM_OBJECT_DEFAULT_MISSING_COUNT = 1;

export const CCODES_OBJECT_DEFAULT_MISSING_COUNT = 1;

export const CLASSIFICATION_CODE_OPTIONS = [
	{
		id: "FOR",
		text: "FOR Codes",
		value: "FOR"
	},
	{
		id: "SEO",
		text: "SEO Codes",
		value: "SEO"
	},
	{
		id: "TOA",
		text: "TOA Codes",
		value: "TOA"
	},
	{
		id: "HERDC",
		text: "HERDC Codes",
		value: "HERDC"
	}
];

export const MAXIMUM_BY_CLASSIFICATION_CODE_TYPE = {
	FOR: 99,
	SEO: 99,
	TOA: 4,
	HERDC: 26,
};

export const DEFAULT_MAXIMUM_BY_CLASSIFICATION_CODE_TYPE = {
	FOR: 6,
	SEO: 6,
	TOA: 4,
	HERDC: 3,
};


export const INVALID_REASON = {
	AD_HOC_TEAM_REMOVED: "AD_HOC_TEAM_REMOVED",
};


export const ADMIN_ONLY_NOTES_MAX_LENGTH = 5000;

export const NOTES_MAX_LENGTH = 5000;


export const PICKLIST_TYPE = {
	AD_HOC_TEAMS: "adhocteams",
	RESEARCH_TEAM_ROLES: "researchTeamRoles",
	ORGANIZATIONS: "organizations",
	UNITS: "units",
};

export const PROPOSAL_SUMMARY_ITEMS = [
	{
		icon: "edit",
		count: 0,
		title: "In Development",
		description: "Proposals are being filled out by researchers"
	},
	{
		icon: "paper-plane",
		count: 0,
		title: "Under Review",
		description: "Proposals are being internally reviewed"
	},
	{
		icon: "thumbs-up",
		count: 0,
		title: "Approved",
		description: "Proposals are approved for submission"
	},
	{
		icon: "university",
		count: 0,
		title: "Submitted to Sponsor",
		description: "Proposals were submitted to sponsor"
	},
	{
		icon: "flag",
		count: 0,
		title: "Under Consideration",
		description: "Sponsors have contacted your institution with interest"
	},
	{
		icon: "money-bill-alt",
		count: 0,
		title: "Funded",
		description: "Proposals were accepted by the sponsor"
	},
	{
		icon: "folder",
		count: 0,
		title: "Closed",
		description: "Proposals were closed by an admin for some other reason"
	},
];

export const PROJECT_SUMMARY_ITEMS = [
	{
		icon: "edit",
		count: 0,
		title: "In Development",
		description: "Initial proposal is being filled out by researchers"
	},
	{
		icon: "money-bill-alt",
		count: 0,
		title: "Active",
		description: "Project has been awarded and research is being conducted"
	},
	{
		icon: "folder-open",
		count: 0,
		title: "In Closeout",
		description: "Project end date has passed and final closeout activities are in progress"
	},
	{
		icon: "folder",
		count: 0,
		title: "Closed",
		description: "Project has been closed out"
	}
];

export const AWARDS_SUMMARY_ITEMS = [
	{
		icon: "copy",
		count: 0,
		title: AWARD_REVIEW_STATUS.setup,
		description: "Award form is being created and filled out"
	},
	{
		icon: "paper-plane",
		count: 0,
		title: AWARD_REVIEW_STATUS.inReview,
		description: "Awards are being internally reviewed; if applicable, negotiations with funding agency in progress"
	},
	{
		icon: "file-invoice-dollar",
		count: 0,
		title: AWARD_REVIEW_STATUS.active,
		description: "Awards are ready to spend against, Project work actively being performed"
	},
	{
		icon: "receipt",
		count: 0,
		title: AWARD_REVIEW_STATUS.closeout,
		description: "Award end date has passed"
	},
	{
		icon: "folder",
		count: 0,
		title: AWARD_REVIEW_STATUS.closed,
		description: "Award closeout has been completed and/or Award was administratively closed"
	},
];

export const SUBAWARDS_SUMMARY_ITEMS = [
	{
		icon: "copy",
		count: 0,
		title: SUBAWARD_REVIEW_STATUS.setup,
		description: "Subaward form is being created and filled out"
	},
	{
		icon: "paper-plane",
		count: 0,
		title: SUBAWARD_REVIEW_STATUS.inReview,
		description: "Subawards are being internally reviewed; if applicable, negotiations with funding agency in progress"
	},
	{
		icon: "file-invoice-dollar",
		count: 0,
		title: SUBAWARD_REVIEW_STATUS.active,
		description: "Subawards are ready to spend against, Project work actively being performed"
	},
	{
		icon: "receipt",
		count: 0,
		title: SUBAWARD_REVIEW_STATUS.closeout,
		description: "Subaward end date has passed"
	},
	{
		icon: "folder",
		count: 0,
		title: SUBAWARD_REVIEW_STATUS.closed,
		description: "Subaward closeout has been completed and/or Subaward was administratively closed"
	},
];

export const DATETIME_FORMATS = new Map([
	[ "en-au", { date: "DD/MM/YYYY", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
	[ "en-ca", { date: "YYYY-MM-DD", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
	[ "en-nz", { date: "DD/MM/YYYY", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
	[ "en-sg", { date: "D/M/YYYY", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
	[ "en-gb", { date: "DD/MM/YYYY", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
	[ "en-us", { date: "M/D/YYYY", time: "hh:mm:ss a", reportTime: "hh:mm:ss TT" } ],
]);

export const DEFAULT_SEARCH_OPTIONS = [
	{
		key: null,
		value: null,
		text: "Write at least 3 characters",
		disabled: true,
	},
];

export const RESEARCH_TEAM_ROLE_VALIDATION_ERROR = {
	EMPTY_FIELD: "Field must not be empty",
	MAXIMUM_CHARACTER_LIMIT_REACHED: "You have reached the 100 character limit. Please edit and try again.",
	NOT_UNIQUE: "Research Team Role must be unique",
};

export * from "./budgetBuilder";
